import { useState } from 'react'
import H from '../data'
import HI from '../components/Icons'
import { Av, Stars, StatusBadge, Delta, Kpi, Banner, Empty, SkeletonRows, BarChart, Sparkline, CardHead, money } from '../components/SharedComponents'

/* ============ CoCafe Host — Reviews ============ */
function ReviewCard({ r, onRespond, userName }) {
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState("");
  return (
    <div className="card card-pad" style={{ borderColor: r.flag === "needs-response" ? "color-mix(in srgb, var(--clay) 32%, var(--border))" : "var(--border)", padding: "8px" }}>
      <div className="row between" style={{ marginBottom: 6, gap: 8 }}>
        <div className="row" style={{ gap: 6, alignItems: 'flex-start', minWidth: 0 }}>
          <Av name={r.guest} c={r.c} size="sm" />
          <div style={{ minWidth: 0 }}>
            <div className="t-xs" style={{ fontWeight: 700 }}>{r.guest}</div>
            <div className="row" style={{ gap: 4, marginTop: 1, fontSize: '10px' }}><Stars value={r.stars} size={11} /><span className="muted">· {r.date}</span></div>
          </div>
        </div>
        {r.flag === "needs-response" && <span className="badge clay" style={{ fontSize: '10px', padding: '2px 6px', flexShrink: 0 }}><span className="bd" />Needs response</span>}
      </div>

      <p className="t-xs" style={{ lineHeight: 1.4, color: "var(--ink-2)", margin: "0 0 6px" }}>"{r.text}"</p>
      <div className="row g4 wrap">{r.tags.map((t) => <span key={t} className="chip" style={{ padding: "2px 6px", fontSize: 9 }}>{t}</span>)}</div>

      {/* response */}
      {r.responded ? (
        <div style={{ marginTop: 6, padding: "6px 8px", background: "var(--surface-2)", borderRadius: "var(--r)", borderLeft: "3px solid var(--green)" }}>
          <div className="row" style={{ gap: 4, marginBottom: 2, alignItems: 'center' }}><Av name={userName} c={0} size="xs" /><span className="t-xs" style={{ fontWeight: 700 }}>You replied</span></div>
          <div className="t-xs" style={{ color: "var(--ink-2)", lineHeight: 1.4 }}>{r.response}</div>
        </div>
      ) : replying ? (
        <div style={{ marginTop: 6 }}>
          <textarea className="ta" placeholder={"Reply to " + r.guest + "…"} value={text} onChange={(e) => setText(e.target.value)} style={{ minHeight: 50, fontSize: '12px' }} />
          <div className="row g6" style={{ marginTop: 6, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setReplying(false)} style={{ fontSize: '11px' }}>Cancel</button>
            <button className="btn btn-pri btn-sm" disabled={!text.trim()} onClick={() => { onRespond(r.id, text); setReplying(false); }} style={{ fontSize: '11px' }}>Post</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, fontSize: '11px' }} onClick={() => setReplying(true)}><HI.reply s={12} /> Respond</button>
      )}
    </div>
  );
}

function Reviews({ toast, user }) {
  const [reviews, setReviews] = useState(H.reviews);
  const [filter, setFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [responseFilter, setResponseFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const total = H.locations[0].reviews;
  const avg = 4.9;
  const maxN = Math.max(...H.ratingBreakdown.map((b) => b.n));
  const userName = user?.name || H.host.name;

  const respond = (id, text) => {
    setReviews((rs) => rs.map((r) => r.id === id ? { ...r, responded: true, response: text + " — " + userName, flag: null } : r));
    toast("Reply posted", "Your response is now public");
  };

  let list = reviews;

  // Apply tab filter
  if (filter === "unanswered") list = reviews.filter((r) => !r.responded);
  else if (filter === "critical") list = reviews.filter((r) => r.stars <= 3);

  // Apply search filter
  if (searchText.trim()) {
    list = list.filter((r) =>
      r.guest.toLowerCase().includes(searchText.toLowerCase()) ||
      r.text.toLowerCase().includes(searchText.toLowerCase())
    );
  }

  // Apply rating filter
  if (ratingFilter !== "all") {
    const ratingNum = parseInt(ratingFilter);
    list = list.filter((r) => r.stars === ratingNum);
  }

  // Apply response filter
  if (responseFilter === "answered") {
    list = list.filter((r) => r.responded);
  } else if (responseFilter === "unanswered") {
    list = list.filter((r) => !r.responded);
  }

  // Pagination
  const totalPages = Math.ceil(list.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedList = list.slice(startIdx, startIdx + itemsPerPage);

  const unanswered = reviews.filter((r) => !r.responded).length;

  return (
    <div className="page fade-in">

      <div>
        {/* rating summary - COMPACT CENTERED */}
        <div className="card" style={{ marginBottom: 20, padding: '12px 16px', textAlign: "center", maxWidth: '320px', margin: '0 auto 20px' }}>
          <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-.02em", lineHeight: 1, marginBottom: 4 }}>{avg}</div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}><Stars value={avg} size={13} /></div>
          <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: 3 }}>Based on {total} reviews</div>
          <div style={{ color: 'var(--green)', fontWeight: 700, fontSize: '11px' }}>Superhost</div>
        </div>

        {/* header + filter tabs - COMPACT */}
        <div className="row between wrap g10" style={{ marginBottom: 12, alignItems: 'center' }}>
          <div className="seg" style={{ padding: '3px', gap: '2px' }}>
            {[["all", "All"], ["unanswered", "Unanswered"], ["critical", "Critical"]].map(([k, l]) => (
              <button key={k} className={filter === k ? "on" : ""} onClick={() => { setFilter(k); setCurrentPage(1); }} style={{ padding: '5px 10px', fontSize: '11px' }}>{l}</button>
            ))}
          </div>
          <span style={{ fontSize: '10px', color: 'var(--muted)' }}>Reply rate: <strong>96%</strong></span>
        </div>

        {/* Search and filter controls - COMPACT */}
        <div style={{ marginBottom: 12, display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            className="inp"
            placeholder="Search reviews..."
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
            style={{ width: '100%', fontSize: '11px', padding: '6px 8px' }}
            aria-label="Search reviews"
          />
          <select
            className="sel"
            value={ratingFilter}
            onChange={(e) => { setRatingFilter(e.target.value); setCurrentPage(1); }}
            aria-label="Filter by rating"
            style={{ fontSize: '11px', padding: '6px 8px', width: '110px' }}
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
          <select
            className="sel"
            value={responseFilter}
            onChange={(e) => { setResponseFilter(e.target.value); setCurrentPage(1); }}
            aria-label="Filter by response status"
            style={{ fontSize: '11px', padding: '6px 8px', width: '110px' }}
          >
            <option value="all">All Reviews</option>
            <option value="unanswered">Unanswered</option>
            <option value="answered">Answered</option>
          </select>
        </div>

        {/* Results counter */}
        <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--muted)' }}>
          {searchText || ratingFilter !== 'all' || responseFilter !== 'all' ? (
            <>{list.length} review{list.length !== 1 ? 's' : ''} found</>
          ) : (
            <>{list.length} review{list.length !== 1 ? 's' : ''}</>
          )}
        </div>

          {list.length === 0 ? (
            <div className="tbl-wrap"><Empty icon={<HI.checkCircle s={22} />} title={filter === "unanswered" ? "All caught up" : "No reviews yet"} desc={searchText || ratingFilter !== 'all' || responseFilter !== 'all' ? "No reviews match your search or filters. Try adjusting your criteria." : filter === "unanswered" ? "You've replied to every review. Nicely done — guests notice hosts who respond." : "Reviews will appear here as guests book and share their experiences."} action={filter !== "unanswered" && <button className="btn btn-pri btn-sm" onClick={() => toast("Opening pricing to improve listings")}>Improve listings</button>} /></div>
          ) : (
            <>
              <div className="col g6">
                {paginatedList.map((r) => (
                <div key={r.id} className="card" style={{ padding: '8px 10px', borderColor: r.flag === "needs-response" ? "color-mix(in srgb, var(--clay) 32%, var(--border))" : "var(--border)" }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Av name={r.guest} c={r.c} size="sm" />
                      <div style={{ fontWeight: 700, fontSize: '11px' }}>{r.guest}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '10px', marginLeft: 'auto' }}>
                      <Stars value={r.stars} size={12} />
                      <span className="muted">· {r.date}</span>
                    </div>
                    {r.flag === "needs-response" && <span className="badge clay" style={{ flexShrink: 0, fontSize: '9px', padding: '2px 5px' }}><span className="bd" />Needs response</span>}
                  </div>
                  <p style={{ lineHeight: 1.5, color: "var(--ink-2)", margin: "0 0 6px", fontSize: '11px' }}>"{r.text}"</p>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>{r.tags.map((t) => <span key={t} style={{ padding: "2px 6px", fontSize: '9px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', whiteSpace: 'nowrap' }}>{t}</span>)}</div>
                  {r.responded ? (
                    <div style={{ padding: "6px 8px", background: "var(--surface-2)", borderRadius: "var(--r-sm)", borderLeft: "3px solid var(--green)", fontSize: '10px' }}>
                      <div className="row g4" style={{ marginBottom: 2, alignItems: 'center' }}><Av name={userName} c={0} size="xs" /><span style={{ fontWeight: 700 }}>You replied</span></div>
                      <div style={{ color: "var(--ink-2)", lineHeight: 1.4 }}>{r.response}</div>
                    </div>
                  ) : (
                    <button className="btn btn-pri btn-sm" onClick={() => { const text = "Thank you for the feedback! We appreciate your visit."; respond(r.id, text); }} style={{ padding: '3px 8px', fontSize: '10px' }}>Reply</button>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  aria-label="Previous page"
                  style={{ padding: '4px 8px', fontSize: '10px' }}
                >
                  ← Prev
                </button>
                <span style={{ fontSize: '10px', color: 'var(--muted)', minWidth: '80px', textAlign: 'center' }}>
                  {currentPage} / {totalPages}
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  aria-label="Next page"
                  style={{ padding: '4px 8px', fontSize: '10px' }}
                >
                  Next →
                </button>
              </div>
            )}
          </>
          )}
      </div>
    </div>
  );
}

export default Reviews;
